import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Flex, Input, Text } from '@chakra-ui/react';
import { DateRangePicker } from 'react-date-range';
import { customStaticRanges } from 'utils/StaticData';
import { enGB } from 'date-fns/locale';
import * as dateFns from 'date-fns';
import { FaChevronDown } from 'react-icons/fa';
import { useDatePickerPosition } from 'hooks/useDatePickerPosition';

const ActivityLogFilter = ({
  search,
  setSearch,
  locationSearch,
  setLocationSearch,
  setPage,
  showCalendar,
  setShowCalendar,
  tempDateRange,
  setTempDateRange,
  calendarFilterTitle,
  setCalendarFilterTitle,
  setTempCalendarFilterTitle,
  handleApply,
  handleClear,
  setDateRange,
  setDebouncedSearch,
  setDebouncedLocationSearch,
}) => {
  const addDays = dateFns.addDays;
  const { datePickerButtonRef, getCalendarPosition } = useDatePickerPosition();

  const handleSelect = (ranges) => {
    const selectedRange = customStaticRanges.find(
      (range) =>
        range.range().startDate.getTime() ===
          ranges.selection.startDate.getTime() &&
        range.range().endDate.getTime() === ranges.selection.endDate.getTime(),
    );

    setTempCalendarFilterTitle(selectedRange?.label || 'Custom');
    setTempDateRange([ranges.selection]);
  };

  const handleReset = () => {
    setPage(1);
    setSearch('');
    setLocationSearch('');
    setDebouncedSearch('');
    setDebouncedLocationSearch('');

    const defaultRange = [
      {
        endDate: new Date(),
        startDate: addDays(new Date(), -6),
        key: 'selection',
      },
    ];
    setTempDateRange(defaultRange);
    setDateRange(defaultRange);
    setCalendarFilterTitle('Last 7 days');
    setTempCalendarFilterTitle('Last 7 days');
    setShowCalendar(false);
  };

  return (
    <>
      <Flex
        alignItems="flex-end"
        flexWrap={'wrap'}
        mb={4}
        gap={{ base: '14px', md: '20px' }}
      >
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text fontSize="xs" fontWeight="600" mb="1" color="gray.400">
            Name
          </Text>
          <Input
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            width={{ base: '100%', md: '200px' }}
            background={'#fff'}
            borderRadius="10px"
          />
        </Box>
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text fontSize="xs" fontWeight="600" mb="1" color="gray.400">
            Location
          </Text>
          <Input
            placeholder="Search by location..."
            value={locationSearch}
            onChange={(e) => setLocationSearch(e.target.value)}
            width={{ base: '100%', md: '200px' }}
            background={'#fff'}
            borderRadius="10px"
          />
        </Box>

        <Box width={{ base: '100%', md: 'auto' }}>
          <Text fontSize="xs" fontWeight="600" mb="1" color="gray.400">
            Login Date Range
          </Text>
          <Box
            position="relative"
            flexShrink="0"
            width={{ base: '100%', md: '200px' }}
          >
            <Button
              ref={datePickerButtonRef}
              py="10px"
              px="15"
              fontWeight="semibold"
              type="button"
              bg="white"
              border="1px solid"
              borderColor="reviveGray"
              color="#292D32"
              borderRadius="8px"
              onClick={() => setShowCalendar(!showCalendar)}
              justifyContent="space-between"
              width={{ base: '100%', md: '200px' }}
            >
              {calendarFilterTitle}
              <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
            </Button>

            {/* Date Range Picker Dropdown */}
            {showCalendar && (
              <>
                <Box
                  position="fixed"
                  bg="rgb(0 0 0 / 40%)"
                  top="0"
                  left="0"
                  right="0"
                  bottom="0"
                  zIndex="999"
                  onClick={() => setShowCalendar(false)}
                ></Box>
                <Box
                  p={{ base: '20px', md: '32px' }}
                  mt="8px"
                  position="fixed"
                  {...getCalendarPosition()}
                  zIndex="1000"
                  bg="white"
                  boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                  borderRadius="20px"
                  className="custom-date-picker"
                  maxWidth={{ base: '95vw', md: '900px' }}
                  width={{ base: '95vw', md: 'auto' }}
                  maxHeight="90vh"
                  overflowY="auto"
                  overflowX="hidden"
                >
                  <DateRangePicker
                    ranges={tempDateRange}
                    onChange={handleSelect}
                    showSelectionPreview={true}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    direction="horizontal"
                    locale={enGB}
                    minDate={new Date(2000, 0, 1)}
                    maxDate={new Date()}
                    staticRanges={customStaticRanges}
                    inputRanges={[]}
                  />
                  <Flex justifyContent="right" gap="12px" marginTop="30px">
                    <Button
                      className="clear-btn"
                      border="1px solid"
                      borderColor="reviveGray"
                      borderRadius="10px"
                      onClick={handleClear}
                      bg="white"
                      _hover={{ bg: 'gray.50' }}
                    >
                      Clear
                    </Button>
                    <Button
                      className="apply-btn"
                      bg="reviveOrange"
                      color="white"
                      borderRadius="10px"
                      onClick={handleApply}
                      _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                    >
                      Apply
                    </Button>
                  </Flex>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* Reset Button */}
        <Box>
          <Button
            onClick={handleReset}
            bg="gray.100"
            color="gray.700"
            borderRadius="8px"
            _hover={{ bg: 'gray.200' }}
          >
            Reset
          </Button>
        </Box>
      </Flex>
    </>
  );
};

ActivityLogFilter.propTypes = {
  search: PropTypes.string,
  setSearch: PropTypes.func,
  locationSearch: PropTypes.string,
  setLocationSearch: PropTypes.func,
  setPage: PropTypes.func,
  showCalendar: PropTypes.bool,
  setShowCalendar: PropTypes.func,
  tempDateRange: PropTypes.array,
  setTempDateRange: PropTypes.func,
  calendarFilterTitle: PropTypes.string,
  setCalendarFilterTitle: PropTypes.func,
  setTempCalendarFilterTitle: PropTypes.func,
  handleApply: PropTypes.func,
  handleClear: PropTypes.func,
  setDateRange: PropTypes.func,
  setDebouncedSearch: PropTypes.func,
  setDebouncedLocationSearch: PropTypes.func,
};

export default ActivityLogFilter;
